// ============================================================
// Sibling Selection Section Component
// ============================================================

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Paper,
  Autocomplete,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import { Plus, Trash2, Users, Search } from 'lucide-react';
import { studentsApi } from '@/api/students.api';

const DEFAULT_SIBLING_ENUMS = [
  { label: 'Brother', value: 'BROTHER' },
  { label: 'Sister', value: 'SISTER' },
  { label: 'Twin Brother', value: 'TWIN_BROTHER' },
  { label: 'Twin Sister', value: 'TWIN_SISTER' },
  { label: 'Step Brother', value: 'STEP_BROTHER' },
  { label: 'Step Sister', value: 'STEP_SISTER' },
  { label: 'Other', value: 'OTHER' },
];

export function SiblingSelectionSection({ formik, schoolId }) {
  const { values, setFieldValue } = formik;
  const siblings = Array.isArray(values.siblings) ? values.siblings : [];

  const [siblingEnums, setSiblingEnums] = useState(DEFAULT_SIBLING_ENUMS);
  const [enumsLoading, setEnumsLoading] = useState(false);

  // Fetch Sibling Enums from API
  useEffect(() => {
    let isMounted = true;
    async function loadEnums() {
      setEnumsLoading(true);
      try {
        const res = await studentsApi.getSiblingEnums();
        const list = Array.isArray(res) ? res : res?.data || [];
        if (isMounted && list.length > 0) {
          setSiblingEnums(list);
        }
      } catch (err) {
        console.warn('Failed to load sibling enums, using fallbacks:', err);
      } finally {
        if (isMounted) setEnumsLoading(false);
      }
    }
    loadEnums();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleAddSibling = () => {
    const newSibling = {
      student_id: '',
      relationship: 'BROTHER',
      student_name: '',
      admission_no: '',
      class_name: '',
      section_name: '',
    };
    setFieldValue('siblings', [...siblings, newSibling]);
  };

  const handleRemoveSibling = (index) => {
    const updated = siblings.filter((_, i) => i !== index);
    setFieldValue('siblings', updated);
  };

  const handleSiblingStudentSelect = (index, student) => {
    // Read the LATEST siblings directly from formik values to avoid stale closure
    const currentSiblings = formik.values.siblings || [];
    const updated = [...currentSiblings];
    if (student) {
      updated[index] = {
        ...updated[index],
        student_id: student.student_id || student.id || '',
        student_name: student.student_name || student.name || '',
        admission_no: student.admission_no || '',
        class_name: student.class?.name || student.class_name || '',
        section_name: student.section?.name || student.section_name || '',
        father_name: student.parent?.father?.name || student.father_name || '',
      };
    } else {
      updated[index] = {
        ...updated[index],
        student_id: '',
        student_name: '',
        admission_no: '',
        class_name: '',
        section_name: '',
        father_name: '',
      };
    }
    setFieldValue('siblings', updated);
  };

  const handleSiblingFieldChange = (index, field, value) => {
    const currentSiblings = formik.values.siblings || [];
    const updated = [...currentSiblings];
    updated[index] = { ...updated[index], [field]: value };
    setFieldValue('siblings', updated);
  };

  return (
    <Box sx={{ width: '100%', mt: 2 }}>
      <Divider sx={{ my: 2 }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Users size={20} color="#3B82F6" />
          <Typography variant="subtitle1" fontWeight={700} color="primary">
            Sibling Information (Optional)
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          startIcon={<Plus size={16} />}
          onClick={handleAddSibling}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Add Sibling
        </Button>
      </Box>

      {siblings.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 2.5,
            textAlign: 'center',
            bgcolor: 'action.hover',
            borderRadius: 2,
            borderStyle: 'dashed',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No sibling details added yet. Click <strong>"Add Sibling"</strong> to link an existing student as a sibling.
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {siblings.map((item, index) => (
          <SiblingRow
              key={index}
              index={index}
              sibling={item}
              schoolId={schoolId}
              siblingEnums={siblingEnums}
              enumsLoading={enumsLoading}
              onStudentSelect={(student) => handleSiblingStudentSelect(index, student)}
              onFieldChange={(field, value) => handleSiblingFieldChange(index, field, value)}
              onRemove={() => handleRemoveSibling(index)}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}

function SiblingRow({ index, sibling, schoolId, siblingEnums, enumsLoading, onStudentSelect, onFieldChange, onRemove }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Prepopulate selectedStudent if editing
  useEffect(() => {
    if (sibling.student_id && sibling.student_name) {
      setSelectedStudent({
        student_id: sibling.student_id,
        student_name: sibling.student_name,
        admission_no: sibling.admission_no || '',
        class: { name: sibling.class_name || '' },
        section: { name: sibling.section_name || '' },
        father_name: sibling.father_name || '',
      });
    }
  }, [sibling.student_id]);

  // Debounced Student Search API Call
  useEffect(() => {
    let active = true;

    if (!inputValue || inputValue.trim().length < 1 || !schoolId) {
      setOptions(selectedStudent ? [selectedStudent] : []);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await studentsApi.searchStudents(schoolId, inputValue.trim());
        const list = Array.isArray(res) ? res : res?.data || [];
        if (active) {
          setOptions(list);
        }
      } catch (err) {
        console.warn('Student search failed:', err);
      } finally {
        if (active) setLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue, schoolId, selectedStudent]);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        {/* Student Search Input */}
        <Grid size={{ xs: 12, sm: 6, md: 7 }}>
          <Autocomplete
            options={options}
            loading={loading}
            value={selectedStudent}
            getOptionLabel={(opt) => {
              if (typeof opt === 'string') return opt;
              if (!opt) return '';
              const name = opt.student_name || opt.name || 'Student';
              const adm = opt.admission_no ? ` (${opt.admission_no})` : '';
              const className = opt.class?.name || opt.class_name ? ` • ${opt.class?.name || opt.class_name}` : '';
              const secName = opt.section?.name || opt.section_name ? ` ${opt.section?.name || opt.section_name}` : '';
              const fatherName = opt.parent?.father?.name || opt.father_name;
              const fatherStr = fatherName ? ` • Father: ${fatherName}` : '';
              return `${name}${adm}${className}${secName}${fatherStr}`;
            }}
            renderOption={(props, option) => {
              const { key, ...otherProps } = props;
              const fatherName = option.parent?.father?.name || option.father_name;
              return (
                <Box component="li" key={key || option.student_id || option.id} {...otherProps}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {option.student_name || option.name || 'Student'}
                      {option.admission_no ? ` (${option.admission_no})` : ''}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {[
                        (option.class?.name || option.class_name) ? `Class ${option.class?.name || option.class_name}` : null,
                        (option.section?.name || option.section_name) ? `Sec ${option.section?.name || option.section_name}` : null,
                        fatherName ? `Father: ${fatherName}` : null,
                      ].filter(Boolean).join(' • ')}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            isOptionEqualToValue={(option, val) => option.student_id === val?.student_id || option.id === val?.id}
            onInputChange={(e, newInputValue) => {
              setInputValue(newInputValue);
            }}
            onChange={(e, newValue) => {
              setSelectedStudent(newValue);
              // Single batch call — no stale closure issue
              onStudentSelect(newValue || null);
            }}
            renderInput={(params) => {
              const inputProps = params.InputProps || params.slotProps?.input || {};
              return (
                <TextField
                  {...params}
                  label={`Sibling Student #${index + 1}`}
                  placeholder="Search sibling by name or admission no..."
                  size="small"
                  fullWidth
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...inputProps,
                      startAdornment: (
                        <>
                          <Search size={16} style={{ marginRight: 6, color: '#9CA3AF', flexShrink: 0 }} />
                          {inputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={18} /> : null}
                          {inputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              );
            }}
          />
        </Grid>

        {/* Relationship Selection Dropdown */}
        <Grid size={{ xs: 10, sm: 5, md: 4 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Relationship"
            value={sibling.relationship || 'BROTHER'}
            onChange={(e) => onFieldChange('relationship', e.target.value)}
          >
            {siblingEnums.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Remove Button */}
        <Grid size={{ xs: 2, sm: 1, md: 1 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton color="error" size="small" onClick={onRemove} title="Remove Sibling">
            <Trash2 size={18} />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
}
